import { useCallback, useRef, useState } from 'react';

export const MAX_VOICE_RECORDING_SECONDS = 60;

function voiceMimeType(): string {
    if (typeof MediaRecorder === 'undefined') return 'audio/webm';
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
    if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4';
    return 'audio/webm';
}

async function transcribeAudio(blob: Blob, language: string): Promise<string> {
    const url = process.env.REACT_APP_VOICE_TRANSCRIBE_URL;
    if (!url) throw new Error('Voice transcription is not configured');
    const res = await fetch(url, {
        method: 'POST',
        body: blob,
        headers: { 'Content-Type': blob.type || 'audio/webm', 'Content-Language': language },
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    const data = (await res.json()) as { text?: string };
    return data.text ?? '';
}

const defaultLanguage = () => 'en';

export function useVoiceCapture(onTranscript: (text: string) => void, getLanguage?: () => string) {
    const [isRecording, setIsRecording] = useState(false);
    const [recordingElapsedSeconds, setRecordingElapsedSeconds] = useState(0);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const mimeTypeRef = useRef<string>('audio/webm');
    const recordingStartRef = useRef<number | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const stopRecordingRef = useRef<() => void>(() => {});
    const wakeLockRef = useRef<WakeLockSentinel | null>(null);

    const releaseWakeLock = useCallback(async () => {
        try {
            await wakeLockRef.current?.release();
            wakeLockRef.current = null;
        } catch {
            wakeLockRef.current = null;
        }
    }, []);

    const stopStream = useCallback(() => {
        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;
    }, []);

    const startRecording = useCallback(async () => {
        if (!navigator.mediaDevices?.getUserMedia) {
            setError('Microphone access is required');
            return;
        }
        try {
            setError(null);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            chunksRef.current = [];
            const mimeType = voiceMimeType();
            const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64000 });
            mediaRecorderRef.current = recorder;
            mimeTypeRef.current = recorder.mimeType || mimeType;
            recorder.ondataavailable = event => {
                if (event.data.size > 0) chunksRef.current.push(event.data);
            };
            recorder.start(250);
            if ('wakeLock' in navigator) {
                try {
                    wakeLockRef.current = await navigator.wakeLock.request('screen');
                } catch {
                    /* ignore */
                }
            }
            recordingStartRef.current = Date.now();
            setRecordingElapsedSeconds(0);
            timerRef.current = setInterval(() => {
                const elapsed = Math.floor((Date.now() - (recordingStartRef.current ?? 0)) / 1000);
                setRecordingElapsedSeconds(Math.min(elapsed, MAX_VOICE_RECORDING_SECONDS));
                if (elapsed >= MAX_VOICE_RECORDING_SECONDS) {
                    if (timerRef.current) clearInterval(timerRef.current);
                    timerRef.current = null;
                    stopRecordingRef.current();
                }
            }, 1000);
            setIsRecording(true);
        } catch {
            setError('Microphone access is required');
        }
    }, []);

    const stopRecording = useCallback(() => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        recordingStartRef.current = null;
        setRecordingElapsedSeconds(0);

        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') {
            setIsRecording(false);
            return;
        }
        recorder.onstop = async () => {
            mediaRecorderRef.current = null;
            stopStream();
            setIsRecording(false);
            if (chunksRef.current.length === 0) {
                void releaseWakeLock();
                return;
            }
            const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
            chunksRef.current = [];
            setIsProcessing(true);
            setError(null);
            try {
                const text = (await transcribeAudio(blob, (getLanguage ?? defaultLanguage)())).trim();
                if (text) onTranscript(text);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Transcription failed');
            } finally {
                setIsProcessing(false);
                void releaseWakeLock();
            }
        };
        recorder.stop();
    }, [getLanguage, onTranscript, releaseWakeLock, stopStream]);

    stopRecordingRef.current = stopRecording;

    const toggle = useCallback(() => {
        if (isRecording) stopRecording();
        else void startRecording();
    }, [isRecording, startRecording, stopRecording]);

    return { isRecording, recordingElapsedSeconds, isProcessing, error, toggle };
}
